import { getLogoutUseCase } from '@/di';
import { authStateManagerService } from '@/infra';
import { type LoaderFunctionArgs, redirect } from 'react-router-dom';

/**
 * Crea un guard para proteger rutas privadas que requieren autenticación.
 *
 * Funcionalidad:
 * - Se ejecuta ANTES del renderizado del componente para evitar flash de contenido no autorizado
 * - Valida que el usuario esté autenticado antes de permitir el acceso a la ruta
 * - Ejecuta un data loader opcional para cargar datos antes de renderizar la página
 *
 * Responsabilidades:
 * - Verificar la autenticación del usuario mediante authStateManagerService
 * - Ejecutar logout si el token es inválido
 * - Redirigir al login si el usuario no está autenticado
 * - Ejecutar el data loader proporcionado si el usuario está autenticado
 *
 * @param dataLoader - Función opcional que carga datos antes del renderizado. Recibe LoaderFunctionArgs y puede retornar datos de forma síncrona o asíncrona
 */
export const privateRouteGuard = <T>(
  dataLoader?: (loaderArgs: LoaderFunctionArgs) => Promise<T> | T,
) => {
  return async (loaderArgs: LoaderFunctionArgs) => {
    if (!authStateManagerService.isUserAuthenticated()) {
      if (!authStateManagerService.hasToken()) throw redirect('/login');

      await getLogoutUseCase().execute('token_invalid');
      throw redirect('/login');
    }

    if (dataLoader) return await dataLoader(loaderArgs);

    return null;
  };
};
